/*
 * Purpose: Workflow state — create, edit, persist, and execute automation flows
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
import { create } from 'zustand';
import { WorkflowExecutor } from '@/services/executor';
import type { ExecutionContext } from '@/services/executor';
import { loadWorkflowsFromStorage, persistWorkflows } from '@/lib/workflowUtils';
import { createMissionStartNode } from '@/lib/missionBlocks';

export type TriggerType = 'webhook' | 'timer' | 'manual' | 'api' | 'gmail_inbound';
export type ToolType = 'http' | 'transform' | 'conditional' | 'delay';
export type ActionType = 'notification' | 'log' | 'webhook' | 'storage';
export type GoalType = 'outcome';
export type StartType = 'mission';
export type AgentType = 'assistant';
export type DroneType = 'remote';
export type TubeType = 'journey';
export type BrandedType =
  | 'bdna_lock'
  | 'squadbox_mega'
  | 'thrpy_dictation'
  | 'sensaffect_emotion';

export interface MissionMeta {
  /** When true, block map cannot change during a run */
  mapLocked?: boolean;
  /** When true (default), AI may replan operational blocks mid-run */
  allowDynamicMap?: boolean;
  lastDynamicPatchAt?: string;
  lastPatchRationale?: string;
}

export interface Node {
  id: string;
  type: 'start' | 'trigger' | 'tool' | 'action' | 'goal' | 'agent' | 'drone' | 'tube' | 'branded';
  subtype:
    | StartType
    | TriggerType
    | ToolType
    | ActionType
    | GoalType
    | AgentType
    | DroneType
    | TubeType
    | BrandedType;
  config: Record<string, unknown>;
  x: number;
  y: number;
}

export interface Edge {
  id: string;
  from: string;
  to: string;
  /** journey_tube = open path from START toward GOAL while outcome unfolds naturally */
  kind?: 'link' | 'journey_tube';
}

export interface Workflow {
  id: string;
  name: string;
  nodes: Node[];
  edges: Edge[];
  createdAt: Date;
  updatedAt: Date;
  missionMeta?: MissionMeta;
}

interface WorkflowStore {
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  selectedNode: string | null;

  createWorkflow: (name: string) => void;
  cloneWorkflow: (workflowId: string, newName: string) => void;
  deleteWorkflow: (workflowId: string) => void;
  setCurrentWorkflow: (workflow: Workflow) => void;
  addNode: (node: Node) => void;
  updateNode: (id: string, updates: Record<string, unknown>) => void;
  deleteNode: (id: string) => void;
  addEdge: (from: string, to: string) => void;
  deleteEdge: (id: string) => void;
  selectNode: (id: string | null) => void;
  saveWorkflow: () => void;
  updateWorkflowName: (name: string) => void;
  updateMissionMeta: (patch: Partial<MissionMeta>) => void;
  executeWorkflow: (signal?: AbortSignal) => Promise<ExecutionContext[]>;
  loadWorkflows: () => void;
  importWorkflow: (workflow: Workflow) => void;
}

const generateId = () => Math.random().toString(36).slice(2, 11);

const initialWorkflows = loadWorkflowsFromStorage();

function syncCurrentWorkflow(
  workflows: Workflow[],
  currentWorkflow: Workflow | null
): { workflows: Workflow[]; currentWorkflow: Workflow | null } {
  if (!currentWorkflow) {
    persistWorkflows(workflows);
    return { workflows, currentWorkflow };
  }

  const exists = workflows.some((w) => w.id === currentWorkflow.id);
  const nextWorkflows = exists
    ? workflows.map((w) => (w.id === currentWorkflow.id ? currentWorkflow : w))
    : [...workflows, currentWorkflow];

  persistWorkflows(nextWorkflows);
  return { workflows: nextWorkflows, currentWorkflow };
}

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  workflows: initialWorkflows,
  currentWorkflow: initialWorkflows[0] ?? null,
  selectedNode: null,

  loadWorkflows: () => {
    const workflows = loadWorkflowsFromStorage();
    set({
      workflows,
      currentWorkflow: get().currentWorkflow ?? workflows[0] ?? null,
    });
  },

  setCurrentWorkflow: (workflow) => {
    set({ currentWorkflow: workflow, selectedNode: null });
  },

  createWorkflow: (name) => {
    const startNode = createMissionStartNode();
    const newWorkflow: Workflow = {
      id: generateId(),
      name,
      nodes: [startNode],
      edges: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      missionMeta: { allowDynamicMap: true, mapLocked: false },
    };

    set((state) => {
      const workflows = [...state.workflows, newWorkflow];
      persistWorkflows(workflows);
      return { workflows, currentWorkflow: newWorkflow, selectedNode: null };
    });
  },

  cloneWorkflow: (workflowId, newName) => {
    set((state) => {
      const source = state.workflows.find((w) => w.id === workflowId);
      if (!source) return state;

      const idMap = new Map<string, string>();
      const nodes = source.nodes.map((node) => {
        const newId = generateId();
        idMap.set(node.id, newId);
        return { ...node, id: newId };
      });

      const edges = source.edges.map((edge) => ({
        id: generateId(),
        from: idMap.get(edge.from) ?? edge.from,
        to: idMap.get(edge.to) ?? edge.to,
      }));

      const clonedWorkflow: Workflow = {
        id: generateId(),
        name: newName,
        nodes,
        edges,
        createdAt: new Date(),
        updatedAt: new Date(),
        missionMeta: source.missionMeta ? { ...source.missionMeta } : undefined,
      };

      const workflows = [...state.workflows, clonedWorkflow];
      persistWorkflows(workflows);
      return { workflows, currentWorkflow: clonedWorkflow, selectedNode: null };
    });
  },

  deleteWorkflow: (workflowId) => {
    set((state) => {
      const workflows = state.workflows.filter((w) => w.id !== workflowId);
      const currentWorkflow =
        state.currentWorkflow?.id === workflowId ? workflows[0] ?? null : state.currentWorkflow;

      persistWorkflows(workflows);
      return { workflows, currentWorkflow, selectedNode: null };
    });
  },

  importWorkflow: (workflow) => {
    set((state) => {
      const imported: Workflow = {
        ...workflow,
        id: generateId(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const workflows = [...state.workflows, imported];
      persistWorkflows(workflows);
      return { workflows, currentWorkflow: imported, selectedNode: null };
    });
  },

  addNode: (node) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        nodes: [...state.currentWorkflow.nodes, node],
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  updateNode: (id, updates) => {
    set((state) => {
      if (!state.currentWorkflow) return state;

      const { x, y, ...configUpdates } = updates;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        nodes: state.currentWorkflow.nodes.map((node) => {
          if (node.id !== id) return node;

          return {
            ...node,
            ...(x !== undefined ? { x: Number(x) } : {}),
            ...(y !== undefined ? { y: Number(y) } : {}),
            ...(Object.keys(configUpdates).length > 0
              ? { config: { ...node.config, ...configUpdates } }
              : {}),
          };
        }),
      };

      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  deleteNode: (id) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        nodes: state.currentWorkflow.nodes.filter((n) => n.id !== id),
        edges: state.currentWorkflow.edges.filter((e) => e.from !== id && e.to !== id),
      };
      return { ...syncCurrentWorkflow(state.workflows, currentWorkflow), selectedNode: null };
    });
  },

  addEdge: (from, to) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        edges: [...state.currentWorkflow.edges, { id: generateId(), from, to }],
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  deleteEdge: (id) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        edges: state.currentWorkflow.edges.filter((e) => e.id !== id),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  selectNode: (id) => {
    set({ selectedNode: id });
  },

  saveWorkflow: () => {
    const { workflows, currentWorkflow } = get();
    if (!currentWorkflow) return;
    const updated = workflows.map((w) =>
      w.id === currentWorkflow.id ? { ...currentWorkflow, updatedAt: new Date() } : w
    );
    persistWorkflows(updated);
    set({ workflows: updated, currentWorkflow: { ...currentWorkflow, updatedAt: new Date() } });
  },

  updateWorkflowName: (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        name: trimmed,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  updateMissionMeta: (patch) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const nextMeta = { ...state.currentWorkflow.missionMeta, ...patch };
      if (patch.mapLocked === true) {
        nextMeta.allowDynamicMap = false;
      }
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        missionMeta: nextMeta,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  executeWorkflow: async (signal) => {
    const { currentWorkflow } = get();
    if (!currentWorkflow) {
      throw new Error('No workflow selected');
    }
    const executor = new WorkflowExecutor(currentWorkflow, { signal });
    return executor.execute();
  },
}));
